import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * The one and only time recovery codes are readable. They are stored hashed on
 * the server, so nothing can bring them back after this dialog closes.
 */
export default function RecoveryCodesDialog({ open, codes, onClose }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open, codes]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const asText = (codes ?? []).join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
    } catch {
      // Clipboard access can be blocked (insecure origin, permissions) —
      // the codes are on screen either way.
      setCopied(false);
    }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([`${asText}\n`], { type: "text/plain" }));
    const link = document.createElement("a");

    link.href = url;
    link.download = "recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-xl bg-surface shadow-xl">
        <div className="px-5 py-4">
          <h2 className="text-sm font-semibold">{t("twoFactor.recovery.title")}</h2>

          <div className="mt-3 rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
            {t("twoFactor.recovery.warning")}
          </div>

          <ul className="mt-4 grid grid-cols-2 gap-2 rounded-lg bg-raised p-3 font-mono text-sm">
            {(codes ?? []).map((code) => (
              <li key={code} className="select-all text-center tracking-wide text-ink" dir="ltr">
                {code}
              </li>
            ))}
          </ul>

          <div className="mt-4 flex gap-2">
            <button type="button" onClick={copy} className="btn-secondary flex-1">
              {copied ? t("twoFactor.recovery.copied") : t("twoFactor.recovery.copy")}
            </button>
            <button type="button" onClick={download} className="btn-secondary flex-1">
              {t("twoFactor.recovery.download")}
            </button>
          </div>
        </div>

        <div className="flex justify-end border-t border-line px-5 py-3">
          <button type="button" onClick={onClose} className="btn-primary">
            {t("twoFactor.recovery.done")}
          </button>
        </div>
      </div>
    </div>
  );
}
